import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "container", 
    "mainImage", 
    "mainImageWrapper", 
    "caption", 
    "count",
    "thumbnail", 
    "thumbnailsContainer",
    "thumbnailsControls" 
  ];
  
  static values = {
    id: String
  }
  
  connect() {
    this.currentIndex = 0;
    this.observeThumbnails();
    
    this.updateScrollControlsVisibility();
    
    this.resizeObserver = new ResizeObserver(entries => {
      this.updateScrollControlsVisibility();
      
      if (this.currentIndex !== undefined && this.thumbnailTargets.length > 0) {
        const activeThumb = this.thumbnailTargets[this.currentIndex];
        if (activeThumb) {
          this.scrollThumbnailIntoView(activeThumb);
        }
      }
    });
    
    this.resizeObserver.observe(this.thumbnailsContainerTarget);
  }
  
  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
  
  openAskedGallery(event) {
    if (event.detail.galleryId === this.idValue) {
      const { startIndex = 0 } = event.detail;
      this.openGallery(startIndex);
    }
  }
  
  openGallery(index = 0) {
    this.containerTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    
    this.showImageAtIndex(index);
    
    setTimeout(() => this.updateScrollControlsVisibility(), 100);
  }
  
  closeGallery() {
    this.containerTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }
  
  handleKeyUp(event) {
    if (this.containerTarget.classList.contains("hidden")) return;
    
    switch (event.key) {
      case "Escape":
        this.closeGallery();
        break;
      case "ArrowLeft":
        this.previousImage();
        break;
      case "ArrowRight":
        this.nextImage();
        break;
    }
  }
  
  nextImage() {
    const newIndex = (this.currentIndex + 1) % this.thumbnailTargets.length;
    this.showImageAtIndex(newIndex);
  }
  
  previousImage() {
    const newIndex = (this.currentIndex - 1 + this.thumbnailTargets.length) % this.thumbnailTargets.length;
    this.showImageAtIndex(newIndex);
  }
  
  selectImage(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    this.showImageAtIndex(index);
  }
  
  showImageAtIndex(index) {
    this.currentIndex = index;
    
    const thumbnail = this.thumbnailTargets[index];
    const src = thumbnail.dataset.src;
    const caption = thumbnail.dataset.caption;
    this.mainImageWrapperTarget.classList.add("loading");
    
    const img = new Image();
    img.onload = () => {
      this.mainImageTarget.src = src;
      this.mainImageTarget.alt = caption || "";
      this.mainImageWrapperTarget.classList.remove("loading");
    };
    img.src = src;
    
    this.captionTarget.textContent = caption || "";
    
    this.thumbnailTargets.forEach((thumb, i) => {
      if (i === index) {
        thumb.classList.add("active");
        this.scrollThumbnailIntoView(thumb);
      } else {
        thumb.classList.remove("active");
      }
    });
  }
  
  scrollThumbnailIntoView(thumbnail) {
    const container = this.thumbnailsContainerTarget;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const thumbnailLeft = thumbnail.offsetLeft;
    const thumbnailWidth = thumbnail.clientWidth;
    const index = this.thumbnailTargets.indexOf(thumbnail);
    
    if (index === 0) {
      container.scrollLeft = 0;
    } else if (thumbnailLeft < scrollLeft) {
      container.scrollLeft = thumbnailLeft;
    } else if (thumbnailLeft + thumbnailWidth > scrollLeft + containerWidth) {
      container.scrollLeft = thumbnailLeft + thumbnailWidth - containerWidth;
    }
  }
  
  scrollThumbnailsLeft() {
    this.thumbnailsContainerTarget.scrollLeft -= 200;
  }
  
  scrollThumbnailsRight() {
    this.thumbnailsContainerTarget.scrollLeft += 200;
  }
  
  observeThumbnails() {
    if ('IntersectionObserver' in window) {
      this.thumbnailObserver = new IntersectionObserver(this.handleThumbnailIntersection.bind(this), {
        root: this.thumbnailsContainerTarget,
        rootMargin: '0px',
        threshold: 0.1
      });
      
      this.thumbnailTargets.forEach(thumbnail => {
        const img = thumbnail.querySelector('img');
        if (img) {
          this.thumbnailObserver.observe(img);
        }
      });
    } else {
      this.thumbnailTargets.forEach(thumbnail => {
        const img = thumbnail.querySelector('img');
        if (img && img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
    }
  }
  
  updateScrollControlsVisibility() {
    const container = this.thumbnailsContainerTarget;
    const isScrollable = container.scrollWidth > container.clientWidth;
    
    if (isScrollable) {
      this.thumbnailsControlsTarget.classList.remove("hidden");
      container.classList.remove("is-centered");
    } else {
      this.thumbnailsControlsTarget.classList.add("hidden");
      container.classList.add("is-centered");
    }
  }
  
  handleThumbnailIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.onload = () => this.updateScrollControlsVisibility();
          img.src = img.dataset.src;
          this.thumbnailObserver.unobserve(img);
        }
      }
    });
  }
}